// 보스: 근접 원형(treant — 고목 수호자·고블린 족장·용암 골렘·묘지기 기사·늪의 히드라)과 시전 원형(ember — 잿불 대주술사·서리 리치·독안개 사제).
// 세계 시간 영역: 이동은 dt×세계 배율로 적분하고, 정수 타이머는 세계 틱에서만 올린다.
// D3a: 보스 정의(content/bosses.ts BOSS_DEFS)가 이름·반경·속도·피해 배율·소환·추가 패턴(돌진·지진·세 갈래 물기·십자·포자)·페이즈 2를 정한다.
// 자유 입장(extras = false)은 원형 패턴·config 수치 그대로다(기존 시험·하네스와 같은 동작).
// S5(STAGE-WORLD-PRD 3.3, 규칙 bossRage.ts): 스테이지 판 중간보스는 체력 30% 이하 분노(공격 사이 간격을 줄여 공격 빈도 ×1.2, 추가 패턴 두 번에 한 번),
// 지역보스는 25% 이하 광폭(공격마다 두 번째 패턴 = 메아리를 같이 깐다). 둘 다 한 번만 들어가고 경고 시간은 줄이지 않는다.
import { BOSS, BOSS_EMBER, BOSS_TIMING, FIXED_DT, HAZARD, HERO, deg } from "../shared/config";
import { Ev, TeleSource } from "../shared/events";
import { XP_BOSS, XpCause } from "../shared/growth";
import { BOSS_DEFS, BOSS_EXTRA, bossIndex, type BossDef, type BossExtra } from "../shared/content/bosses";
import { BossKind, BossState, Scene, combatScene, TeleShape, type BossStateId, type TeleShapeId } from "../shared/types";
import { telegraphHit } from "./combat";
import type { GameSim } from "./game";
import type { BossKey } from "../shared/levels";
import { TIMER_MAX } from "./state";
import { BossRage, RAGE_EVENT, echoFor, echoWindup, frenzyCooldown, rageCooldown, rageKindOf, rageThreshold, type EchoKind } from "./bossRage";

// 수치는 shared/config(COMBAT-FEEL 3.1)에서 온다. 경직은 최대 체력 10% 누적 피해마다 1.5초.
export { BOSS_TIMING };
export const BOSS_STAGGER_DAMAGE = BOSS.hp * BOSS.staggerRatio;
const SLAM_RADIUS = BOSS.slamRadius, SWEEP_RANGE = BOSS.sweepRange, SWEEP_ARC = deg(BOSS.sweepArcDeg);
const ATTACK_REACH = BOSS.attackReach, SUMMON_RING = BOSS.summonRing;
/** 휩쓸기는 영웅 중심점이 부채꼴 안일 때만 맞으므로, 이 거리 안에서만 고른다(밖이면 내려찍기) */
const SWEEP_TRIGGER = SWEEP_RANGE * 0.85;
/** 추가 패턴 경고 슬롯 최대 수(포자 5) */
const EXTRA_SLOTS = 6;
/** 광폭 두 번째 패턴(메아리) 경고 슬롯 최대 수(균열 3줄) */
const ECHO_SLOTS = 3;
/** 메아리 예약표 주인 번호(시전형 패턴 100~105와 겹치지 않게) */
const ECHO_OWNER = 120;
/** 추가 패턴 번호(Ev.BossPattern a). 0~3 = 원형(내려찍기·고리 / 휩쓸기·균열 / 운석 / 소환), 9 = 페이즈 2 진입 */
// 10~12는 분노·광폭(bossRage RAGE_EVENT)이 쓴다. C2 패턴은 13부터
export const BossPatternId = { charge: 4, quake: 5, bite3: 6, cross: 7, spores: 8, phase2: 9, lance3: 13, execute: 14, vortex: 15, star: 16 } as const;
const EXTRA_ID: Readonly<Record<BossExtra, number>> = { charge: 4, quake: 5, bite3: 6, cross: 7, spores: 8, lance3: 13, execute: 14, vortex: 15, star: 16 };
/** 시전형 추가 패턴(포자·십자·소용돌이·별빛) */
type EmberExtra = 'cross' | 'spores' | 'vortex' | 'star';
const isEmberExtra = (x: BossExtra | null): x is EmberExtra => x === 'cross' || x === 'spores' || x === 'vortex' || x === 'star';
/** 근접형 추가 패턴 */
type MeleeExtra = 'charge' | 'quake' | 'bite3' | 'lance3' | 'execute';
const isMeleeExtra = (x: BossExtra | null): x is MeleeExtra => x === 'charge' || x === 'quake' || x === 'bite3' || x === 'lance3' || x === 'execute';

export class BossController {
  private readonly emberSlots = new Int16Array(EXTRA_SLOTS).fill(-1);
  private readonly emberDue = new Float64Array(EXTRA_SLOTS);
  private patternStart = 0;
  private emberPattern = 0;
  private meteorStarted = 0;
  private meteorResolved = 0;
  private telegraph = -1;
  private cooldown = 0;
  private staggerDamage = 0;
  private summonsDone = 0;
  private pendingSummons = 0;
  private attacks = 0;
  private targetX = 0;
  private targetZ = 0;
  /** 그로기 남은 틱(세계 시간). 0이면 그로기 아님 */
  private groggyTicks = 0;
  /** 지금 보스 정의·피해 배율·추가 패턴 사용 여부 */
  private def: BossDef = BOSS_DEFS.treant;
  private dmg = 1;
  private extras = false;
  /** 근접형 추가 패턴 진행(0 = 없음): 종류, 슬롯별 판정 틱(stateTick 기준), 돌진 방향·남은 거리 */
  private xKind: BossExtra | null = null;
  private readonly xSlots = new Int16Array(EXTRA_SLOTS).fill(-1);
  private readonly xDue = new Int32Array(EXTRA_SLOTS);
  private xCount = 0;
  private xResolved = 0;
  private xHit = false;
  private dashAngle = 0;
  private dashLeft = 0;
  /** 광폭 메아리: 종류(null = 없음), 슬롯, 진행 틱(세계 정수 틱), 판정 틱, 맞혔는지 */
  private echoKind: EchoKind | null = null;
  private readonly echoSlots = new Int16Array(ECHO_SLOTS).fill(-1);
  private echoCount = 0;
  private echoTick = 0;
  private echoDue = 0;
  constructor(private readonly g: GameSim) {}

  /**
   * 그로기 훅(COMBAT-FEEL 2.7, PRD 3A.2): 보스는 그로기일 때만 띄움·다운·잡기를 받는다.
   * 그로기 게이지(강공·잡기·피니시로 깎아 0이면 6초 무력화)는 아직 없으므로 항상 false이고, 게이지가 생기면 0이 되는 순간 startGroggy를 부른다.
   * 이번 회차에는 보스 띄움 동작 자체가 없어 훅이 켜져도 보스 상태는 바뀌지 않는다.
   */
  get groggy(): boolean { return this.groggyTicks > 0; }
  /** 띄움·다운을 허용해도 되는 보스 상태(그로기 중이고 피해를 받을 수 있을 때) */
  get canLaunch(): boolean { return this.groggy && this.vulnerable; }
  startGroggy(ticks: number): void { this.groggyTicks = Math.max(0, Math.round(ticks)); }
  /** 지금 보스 정의(시험·HUD) */
  get current(): BossDef { return this.def; }

  get vulnerable(): boolean {
    const b = this.g.boss, s = b.state;
    // 레이드 비룡은 공중(비행 순환)에서 피해를 받지 않는다(invulnerable — 등장·사망도 같은 표지)
    return s !== BossState.Inactive && s !== BossState.Intro && s !== BossState.Dying && s !== BossState.Dead && !(b.kind === BossKind.Wyvern && b.invulnerable);
  }

  reset(): void {
    this.cancelEmber();
    this.cancelExtra();
    this.cancelEcho();
    this.g.releaseTelegraph(this.telegraph);
    this.telegraph = -1; this.cooldown = 0; this.groggyTicks = 0; this.staggerDamage = 0; this.summonsDone = 0; this.pendingSummons = 0; this.attacks = 0;
    this.def = BOSS_DEFS.treant; this.dmg = 1; this.extras = false;
    const b = this.g.boss;
    b.state = BossState.Inactive; b.stateTick = 0; b.hp = 0; b.maxHp = BOSS.hp; b.hitTick = 0xffff;
    b.kind = BossKind.Treant; b.variant = 0; b.phase2 = false; b.rage = BossRage.None; b.rageAt = 0; b.invulnerable = false; b.stagger = 0; b.radius = BOSS.radius; b.name = BOSS.name; b.air = 0;
  }

  /**
   * 보스 등장. key = 보스 정의, hp = 최대 체력(0 = 원형 config 값), dmg = 피해 배율(스테이지 × 지역 × 보스), extras = 추가 패턴·페이즈 2 사용.
   * 자유 입장 기본값(hp 0, dmg 1, extras false)은 기존 고목 수호자·잿불 대주술사와 같다.
   */
  enter(x: number, z: number, key: BossKey = "treant", hp = 0, dmg = 1, extras = false): void {
    this.reset();
    const b = this.g.boss, def = BOSS_DEFS[key] ?? BOSS_DEFS.treant;
    this.def = def; this.dmg = dmg > 0 ? dmg : 1; this.extras = extras;
    b.kind = def.archetype === "ember" ? BossKind.Ember : BossKind.Treant;
    b.variant = bossIndex(def.key);
    const base = b.kind === BossKind.Ember ? BOSS_EMBER.hp : BOSS.hp;
    b.name = def.name; b.radius = def.radius;
    b.rageAt = rageThreshold(def, extras);
    b.x = b.prevX = x; b.z = b.prevZ = z; b.hp = b.maxHp = hp > 0 ? hp : base; b.facing = Math.PI;
    this.setState(BossState.Intro);
    const e = this.g.emit(Ev.BossIntro); e.x = x; e.z = z; e.a = b.variant;
  }

  private setState(state: BossStateId): void {
    const b = this.g.boss;
    this.cancelEmber();
    this.cancelExtra();
    this.g.releaseTelegraph(this.telegraph);
    this.telegraph = -1;
    this.dashLeft = 0;
    b.state = state; b.stateTick = 0;
    // 광폭 메아리는 첫 패턴과 따로 돌지만, 경직·사망 때는 같이 거둔다(묶인 보스의 공격이 터지지 않게)
    if (state === BossState.Stagger || state === BossState.Dying || state === BossState.Dead || state === BossState.Inactive) this.cancelEcho();
    // 등장 연출·사망 중에는 피해를 받지 않는다.
    b.invulnerable = state === BossState.Intro || state === BossState.Dying || state === BossState.Dead;
    b.stagger = state === BossState.Stagger ? 1 : Math.min(1, this.staggerDamage / this.staggerThreshold);
  }

  damaged(amount: number): void {
    const g = this.g, b = g.boss;
    // 레이드 비룡(RAID-PRD 5절): 페이즈·경직·쓰러짐은 레이드 두뇌가 정한다(보스 EXP·바닥 드롭 없음 — 보상은 레이드 정산)
    if (b.kind === BossKind.Wyvern) { g.raid.bossDamaged(amount); return; }
    if (b.hp <= 0) {
      this.setState(BossState.Dying);
      const e = g.emit(Ev.BossDefeated); e.x = b.x; e.z = b.z; e.a = b.variant;
      g.grantXp(XP_BOSS[g.regionKey], XpCause.Boss, b.x, b.z);
      // 보스 확정 드롭(D2a): 보스 풀(전설은 여기에만)
      g.loot.onBossKilled(b.x, b.z);
      // 아바타 드롭(레인 E): 전용 난수 — 위 장비 드롭 수열과 별개
      g.avatarBossDrop();
      // 모험 목표판 현상금(레인 E 2단계): 오늘 대상이면 완료 + 추가 득템 판정
      g.adventureBossKill(b.x, b.z);
      return;
    }
    const summonAt = this.def.summonAt;
    for (let i = 0; i < summonAt.length; i++) {
      const bit = 1 << i;
      if (!(this.summonsDone & bit) && b.hp <= b.maxHp * summonAt[i]) { this.summonsDone |= bit; this.pendingSummons++; }
    }
    // 페이즈 2(지역보스, 스테이지 판): 체력 비율 이하에서 한 번. 추가 패턴이 더해지고 더 자주 쓴다
    if (this.extras && !b.phase2 && this.def.phase2 > 0 && b.hp <= b.maxHp * this.def.phase2) {
      b.phase2 = true;
      this.patternEvent(BossPatternId.phase2);
    }
    // S5 분노·광폭: 문턱 이하에서 한 번(스테이지 판만, rageAt 0이면 없음). 지금 진행 중인 패턴은 그대로 두고 다음 공격부터 적용
    if (b.rage === BossRage.None && b.rageAt > 0 && b.hp <= b.maxHp * b.rageAt) {
      b.rage = rageKindOf(this.def, this.extras);
      this.patternEvent(b.rage === BossRage.Rage ? RAGE_EVENT.rage : RAGE_EVENT.frenzy);
    }
    // 경직 중 받은 피해는 경직 게이지에 쌓지 않는다(경직 연쇄 방지).
    if (b.state === BossState.Stagger) return;
    this.staggerDamage += amount;
    if (this.staggerDamage >= this.staggerThreshold) {
      this.staggerDamage -= this.staggerThreshold;
      this.setState(BossState.Stagger);
      const e = g.emit(Ev.GuardBreak);
      e.x = b.x; e.z = b.z; e.a = 1; e.b = 0; e.c = BOSS_TIMING.stagger;
    } else b.stagger = this.staggerDamage / this.staggerThreshold;
  }

  /** 지금 쓸 수 있는 추가 패턴 목록에서 이번 차례 하나(없으면 null) */
  private nextExtra(): BossExtra | null {
    if (!this.extras) return null;
    const d = this.def, n = d.extra.length + (this.g.boss.phase2 ? d.phase2Extra.length : 0);
    if (n === 0) return null;
    const k = Math.floor(this.attacks / (this.fastExtra ? 2 : 3)) % n;
    return k < d.extra.length ? d.extra[k] : d.phase2Extra[k - d.extra.length];
  }
  /** 추가 패턴을 두 번에 한 번 쓰는지(페이즈 2·분노) */
  private get fastExtra(): boolean { return this.g.boss.phase2 || this.g.boss.rage === BossRage.Rage; }
  /** 이번 공격 차례가 추가 패턴인지: 평소 세 번에 한 번, 페이즈 2·분노는 두 번에 한 번 */
  private extraTurn(): boolean {
    const fast = this.fastExtra;
    return this.extras && this.attacks % (fast ? 2 : 3) === (fast ? 1 : 2);
  }
  /** 공격 뒤 재사용 대기(틱): 분노는 공격 빈도 ×1.2가 되게 줄이고, 광폭은 두 패턴을 같이 쓰는 대신 늘린다 */
  private nextCooldown(): number {
    const b = this.g.boss, arch = this.def.archetype;
    if (b.rage === BossRage.Rage) return rageCooldown(arch);
    const mult = this.def.cooldownMult ?? 1;
    if (b.rage === BossRage.Frenzy) return frenzyCooldown(arch, mult);
    return Math.round((arch === "ember" ? BOSS_EMBER.attackCooldown : BOSS_TIMING.attackCooldown) * mult);
  }

  /** 세계 시간 1스텝 */
  step(rate: number, whole: boolean): void {
    const g = this.g, b = g.boss, h = g.hero, state = b.state;
    if (rate <= 0 || state === BossState.Inactive || state === BossState.Dead) return;
    if (b.kind === BossKind.Wyvern) { g.raid.stepBoss(rate, whole); return; }
    if (whole) {
      if (this.echoKind !== null && this.echoTick < TIMER_MAX) this.echoTick++;
      if (b.stateTick < TIMER_MAX) b.stateTick++;
      if (b.hitTick < TIMER_MAX) b.hitTick++;
      if (this.cooldown > 0) this.cooldown--;
      if (this.groggyTicks > 0) this.groggyTicks--;
    }
    if (state === BossState.Intro) { if (b.stateTick >= BOSS_TIMING.intro) { this.setState(BossState.Chase); if (b.kind === BossKind.Ember) this.cooldown = BOSS_EMBER.attackCooldown; } return; }
    if (state === BossState.Dying) {
      if (b.stateTick >= BOSS_TIMING.dying) { this.setState(BossState.Dead); g.flowController.requestClear(); }
      return;
    }
    if (state === BossState.Stagger) { if (b.stateTick >= BOSS_TIMING.stagger) this.setState(BossState.Chase); return; }
    if (this.echoKind !== null) { this.stepEcho(); if (!combatScene(g.flow.scene)) return; }
    if (state === BossState.Summon) { if (b.stateTick >= BOSS_TIMING.summon) this.setState(BossState.Chase); return; }
    if (b.kind === BossKind.Ember) { this.stepEmber(rate); return; }
    if (this.xKind !== null) { this.stepExtra(); return; }
    if (state === BossState.Slam && this.dashLeft > 0) { this.stepDash(rate); return; }
    if (state === BossState.SlamWindup || state === BossState.SweepWindup) {
      const total = state === BossState.SlamWindup ? BOSS_TIMING.slamWindup : BOSS_TIMING.sweepWindup;
      if (this.telegraph >= 0) g.telegraphs[this.telegraph].tick = Math.min(total, b.stateTick + g.worldFrac);
      if (b.stateTick >= total) {
        const slot = this.telegraph;
        // 경고 데칼 모양 그대로 판정한다(영웅 중심점 기준, 데칼 밖이면 안전).
        const hit = slot >= 0 && telegraphHit(g.telegraphs[slot], h.x, h.z);
        this.telegraph = -1;
        g.fireTelegraph(slot);
        this.setState(state === BossState.SlamWindup ? BossState.Slam : BossState.Sweep);
        this.cooldown = this.nextCooldown();
        if (hit) g.combat.hurtHero(BOSS.damage * this.dmg, b.x, b.z, true, 1);
      }
      return;
    }
    if (state === BossState.Slam || state === BossState.Sweep) { if (b.stateTick >= BOSS_TIMING.strike) this.setState(BossState.Chase); return; }
    if (this.pendingSummons > 0) { this.pendingSummons--; this.summon(); return; }
    const dx = h.x - b.x, dz = h.z - b.z, dist = Math.hypot(dx, dz);
    if (dist > 1e-6) b.facing = Math.atan2(dx, dz);
    if (this.cooldown === 0 && dist <= ATTACK_REACH + (this.def.radius - BOSS.radius) && this.beginAttack(dist)) return;
    if (b.state !== BossState.Chase) this.setState(BossState.Chase);
    if (dist > b.radius + HERO.radius + 0.4) {
      const step = this.def.speed * FIXED_DT * rate, sx = (dx / dist) * step, sz = (dz / dist) * step;
      if (g.walkableBoss(b.x + sx, b.z, b.radius)) b.x += sx;
      if (g.walkableBoss(b.x, b.z + sz, b.radius)) b.z += sz;
    }
  }

  private summon(): void {
    const g = this.g, b = g.boss;
    this.setState(BossState.Summon);
    this.patternEvent(3);
    const ember = b.kind === BossKind.Ember;
    const kinds = this.def.summonKinds, count = kinds.length;
    const ring = ember ? BOSS_EMBER.summonRing : Math.max(SUMMON_RING, b.radius + 2.5);
    const room = g.map.rooms[g.flow.roomIndex];
    for (let i = 0; i < count; i++) {
      const angle = (i * Math.PI * 2) / count;
      let x = b.x + Math.sin(angle) * ring, z = b.z + Math.cos(angle) * ring;
      if (room) {
        x = Math.max(room.area.x0 + 1, Math.min(room.area.x1 - 1, x));
        z = Math.max(room.area.z0 + 1, Math.min(room.area.z1 - 1, z));
      }
      // 기둥·벽에 걸린 위치는 주변 유효 위치를 찾는다.
      g.flowController.spawnNear(kinds[i], false, x, z, ember ? 0 : -1);
    }
  }

  private get staggerThreshold(): number {
    const b = this.g.boss;
    return b.maxHp * (b.kind === BossKind.Ember ? BOSS_EMBER.staggerRatio : BOSS.staggerRatio);
  }

  private patternEvent(pattern: number): void {
    const e = this.g.emit(Ev.BossPattern), b = this.g.boss;
    e.x = b.x; e.z = b.z; e.a = pattern; e.b = b.kind; e.c = b.variant;
  }

  private cancelEmber(): void {
    for (let i = 0; i < this.emberSlots.length; i++) {
      this.g.releaseTelegraph(this.emberSlots[i]); this.emberSlots[i] = -1;
      if (this.g.map.threatClock) this.g.threats.cancel(100 + i);
    }
    this.meteorStarted = this.meteorResolved = 0;
  }

  private cancelExtra(): void {
    for (let i = 0; i < this.xSlots.length; i++) { this.g.releaseTelegraph(this.xSlots[i]); this.xSlots[i] = -1; }
    this.xKind = null; this.xCount = 0; this.xResolved = 0; this.xHit = false;
  }

  private emberTelegraph(i: number, shape: TeleShapeId, x: number, z: number, radius: number, angle: number, width: number, total: number): boolean {
    const slot = this.g.allocateTelegraph(TeleSource.Boss, BossKind.Ember);
    if (slot < 0) return false;
    this.emberSlots[i] = slot;
    const t = this.g.telegraphs[slot];
    t.shape = shape; t.x = x; t.z = z; t.radius = radius; t.angle = angle; t.width = width; t.tick = 0; t.total = total;
    return true;
  }

  /** 시전형 패턴 경고 수: 0 고리 1, 1 균열 3, 2 운석 3(차례로), 3 추가(십자 4 · 포자 5 · 소용돌이 6 · 별빛 6) */
  private emberCount(pattern: number, extra: BossExtra | null): number {
    if (pattern === 0) return 1;
    if (pattern === 1) return BOSS_EMBER.fissureCount;
    if (pattern === 2) return BOSS_EMBER.meteorCount;
    return extra === "spores" ? 5 : extra === "vortex" || extra === "star" ? 6 : 4;
  }

  private beginEmber(dist: number): boolean {
    const g = this.g, b = g.boss, E = BOSS_EMBER, now = g.threats.now;
    // 추가 패턴(십자·포자): 정해진 차례에만. 고리(가까이)가 우선
    const extra = dist > E.novaTrigger && this.extraTurn() ? this.nextExtra() : null;
    const ex = isEmberExtra(extra) ? extra : null;
    const pattern = dist <= E.novaTrigger ? 0 : ex ? 3 : this.attacks % 2 === 0 ? 2 : 1;
    const total = pattern === 0 ? E.novaWindup : pattern === 1 ? E.fissureWindup : pattern === 2 ? E.meteorWindup : BOSS_EXTRA[ex!].windup;
    const need = pattern === 0 ? 1 : pattern === 3 ? this.emberCount(3, ex) : 3;
    let free = 0;
    for (let i = 0; i < g.telegraphs.length; i++) if (!g.telegraphs[i].active) free++;
    if (free < need) return false;
    this.setState(pattern === 0 ? BossState.SlamWindup : pattern === 1 || pattern === 3 ? BossState.SweepWindup : BossState.Cast);
    const count = pattern === 2 ? E.meteorCount : 1;
    for (let i = 0; i < count; i++) {
      this.emberDue[i] = now + total + (pattern === 2 ? i * E.meteorGap : 0);
      if (!g.threats.reserve(100 + i, this.emberDue[i])) { this.setState(BossState.Chase); return false; }
    }
    this.emberPattern = pattern; this.patternStart = now;
    if (pattern !== 0) this.attacks++;
    if (pattern === 0) this.emberTelegraph(0, TeleShape.Circle, b.x, b.z, E.novaRadius, 0, 0, total);
    else if (pattern === 1) {
      for (let i = 0; i < E.fissureCount; i++) this.emberTelegraph(i, TeleShape.Line, b.x, b.z, E.fissureLength, b.facing + deg((i - 1) * E.fissureSpreadDeg), E.fissureWidth, total);
    } else if (pattern === 3 && ex === "cross") {
      // 십자: 바라보는 방향에서 45° 비튼 대각 4줄(정면으로 다가오면 줄 사이 틈이 안전하다)
      const C = BOSS_EXTRA.cross;
      for (let i = 0; i < 4; i++) this.emberTelegraph(i, TeleShape.Line, b.x, b.z, C.length, b.facing + Math.PI / 4 + i * Math.PI / 2, C.width, total);
    } else if (pattern === 3 && ex === "star") {
      // 별빛(C2): 보스에서 60°마다 6줄. 바라보는 방향에서 30° 비틀어 정면 접근 길은 줄 사이 틈이다
      const St = BOSS_EXTRA.star;
      for (let i = 0; i < 6; i++) this.emberTelegraph(i, TeleShape.Line, b.x, b.z, St.length, b.facing + Math.PI / 6 + i * Math.PI / 3, St.width, total);
    } else if (pattern === 3 && ex === "vortex") {
      // 소용돌이(C2): 보스 둘레 ring m 고리에 원 6개. 보스에 바짝 붙거나 고리 바깥으로 나가면 안전하다
      const V = BOSS_EXTRA.vortex;
      for (let i = 0; i < 6; i++) {
        const a = b.facing + i * Math.PI / 3;
        this.emberTelegraph(i, TeleShape.Circle, b.x + Math.sin(a) * V.ring, b.z + Math.cos(a) * V.ring, V.radius, 0, 0, total);
      }
    } else if (pattern === 3) {
      // 포자: 영웅 자리 + 네 대각(ring m). 원 사이 틈으로 빠져나간다
      const S = BOSS_EXTRA.spores, hx = g.hero.x, hz = g.hero.z;
      this.emberTelegraph(0, TeleShape.Circle, hx, hz, S.radius, 0, 0, total);
      for (let i = 0; i < 4; i++) {
        const a = Math.PI / 4 + i * Math.PI / 2;
        this.emberTelegraph(i + 1, TeleShape.Circle, hx + Math.sin(a) * S.ring, hz + Math.cos(a) * S.ring, S.radius, 0, 0, total);
      }
    } else {
      this.emberTelegraph(0, TeleShape.Circle, g.hero.x, g.hero.z, E.meteorRadius, 0, 0, total);
      this.meteorStarted = 1;
    }
    this.emberExtra = ex;
    const id = pattern === 3 ? EXTRA_ID[ex!] : pattern;
    this.patternEvent(id);
    this.startEcho(id, pattern === 2 ? total + (E.meteorCount - 1) * E.meteorGap : total);
    return true;
  }
  private emberExtra: BossExtra | null = null;

  private stepEmber(rate: number): void {
    const g = this.g, b = g.boss, h = g.hero, E = BOSS_EMBER, now = g.threats.now;
    if (b.state === BossState.Cast || b.state === BossState.SlamWindup || b.state === BossState.SweepWindup) {
      if (this.emberPattern === 2) {
        if (this.meteorStarted < E.meteorCount && now >= this.patternStart + this.meteorStarted * E.meteorGap) {
          if (!this.emberTelegraph(this.meteorStarted, TeleShape.Circle, h.x, h.z, E.meteorRadius, 0, 0, E.meteorWindup)) { this.setState(BossState.Chase); return; }
          this.meteorStarted++;
        }
        for (let i = 0; i < this.meteorStarted; i++) {
          const slot = this.emberSlots[i];
          if (slot < 0) continue;
          const t = g.telegraphs[slot];
          t.tick = Math.min(t.total, t.total - (this.emberDue[i] - now) + g.worldFrac);
          if (now < this.emberDue[i]) continue;
          const hit = telegraphHit(t, h.x, h.z), x = t.x, z = t.z;
          g.fireTelegraph(slot); this.emberSlots[i] = -1; this.meteorResolved++;
          if (hit) g.combat.hurtHero(E.meteorDamage * this.dmg, x, z, false, 2);
          if (!combatScene(g.flow.scene)) return;
        }
        if (this.meteorResolved === E.meteorCount) { this.setState(BossState.Chase); this.cooldown = this.nextCooldown(); }
      } else {
        const count = this.emberCount(this.emberPattern, this.emberExtra);
        let hit = false;
        for (let i = 0; i < count; i++) {
          const slot = this.emberSlots[i];
          if (slot < 0) continue;
          const t = g.telegraphs[slot];
          t.tick = Math.min(t.total, t.total - (this.emberDue[0] - now) + g.worldFrac);
          if (now >= this.emberDue[0]) { hit = hit || telegraphHit(t, h.x, h.z); g.fireTelegraph(slot); this.emberSlots[i] = -1; }
        }
        if (now >= this.emberDue[0]) {
          const nova = this.emberPattern === 0, ex = this.emberPattern === 3 ? this.emberExtra : null;
          this.setState(nova ? BossState.Slam : BossState.Sweep); this.cooldown = this.nextCooldown();
          const base = nova ? E.novaDamage : ex === "cross" ? E.fissureDamage * BOSS_EXTRA.cross.dmg : ex === "spores" ? E.meteorDamage * BOSS_EXTRA.spores.dmg
            : ex === "vortex" ? E.meteorDamage * BOSS_EXTRA.vortex.dmg : ex === "star" ? E.fissureDamage * BOSS_EXTRA.star.dmg : E.fissureDamage;
          if (hit && g.combat.hurtHero(base * this.dmg, b.x, b.z, false, 2) && ex) g.hazards.afflict(g.map.hazardTheme);
        }
      }
      return;
    }
    if (b.state === BossState.Slam || b.state === BossState.Sweep) { if (b.stateTick >= BOSS_TIMING.strike) this.setState(BossState.Chase); return; }
    if (this.pendingSummons > 0) { this.pendingSummons--; this.summon(); return; }
    const dx = h.x - b.x, dz = h.z - b.z, dist = Math.hypot(dx, dz);
    if (dist > 1e-6) b.facing = Math.atan2(dx, dz);
    if (this.cooldown === 0 && this.beginEmber(dist)) return;
    if (dist < 1e-6) return;
    const speed = this.def.speed * FIXED_DT * rate * (dist < E.keepMin ? -1 : dist > E.keepMax ? 1 : 0);
    const sx = dx / dist * speed, sz = dz / dist * speed;
    if (g.walkableBoss(b.x + sx, b.z, b.radius)) b.x += sx;
    if (g.walkableBoss(b.x, b.z + sz, b.radius)) b.z += sz;
  }

  private beginAttack(dist: number): boolean {
    const g = this.g, b = g.boss;
    // 추가 패턴 차례(D3a): 근접형은 돌진·지진·세 갈래 물기
    const extra = this.extraTurn() ? this.nextExtra() : null;
    if (isMeleeExtra(extra)) return this.beginExtra(extra);
    const slot = g.allocateTelegraph(TeleSource.Boss, BossKind.Treant);
    if (slot < 0) return false;
    // 내려찍기·휩쓸기 교대. 휩쓸기 차례라도 부채꼴에 닿지 않는 거리면 내려찍기로 바꾼다.
    const slam = this.attacks++ % 2 === 0 || dist > SWEEP_TRIGGER + (b.radius - BOSS.radius);
    this.setState(slam ? BossState.SlamWindup : BossState.SweepWindup);
    this.patternEvent(slam ? 0 : 1);
    this.telegraph = slot;
    this.targetX = g.hero.x; this.targetZ = g.hero.z;
    const t = g.telegraphs[slot];
    const grow = this.extras ? b.radius / BOSS.radius : 1;
    t.shape = slam ? TeleShape.Circle : TeleShape.Cone;
    t.x = slam ? this.targetX : b.x; t.z = slam ? this.targetZ : b.z;
    t.radius = slam ? SLAM_RADIUS * Math.max(0.75, Math.min(1.2, grow)) : SWEEP_RANGE * Math.max(0.75, Math.min(1.2, grow)); t.width = slam ? 0 : SWEEP_ARC; t.angle = b.facing;
    t.tick = 0; t.total = slam ? BOSS_TIMING.slamWindup : BOSS_TIMING.sweepWindup;
    this.startEcho(slam ? 0 : 1, t.total);
    return true;
  }

  /** 근접형 추가 패턴 시작: 경고를 모두 깔고 슬롯별 판정 틱을 정한다 */
  private beginExtra(kind: MeleeExtra): boolean {
    const g = this.g, b = g.boss;
    const need = kind === "bite3" || kind === "lance3" ? 3 : 1;
    let free = 0;
    for (let i = 0; i < g.telegraphs.length; i++) if (!g.telegraphs[i].active) free++;
    if (free < need) return false;
    this.setState(kind === "quake" || kind === "execute" ? BossState.SlamWindup : BossState.SweepWindup);
    this.attacks++;
    this.xKind = kind; this.xCount = need; this.xResolved = 0; this.xHit = false;
    const facing = b.facing;
    for (let i = 0; i < need; i++) {
      const slot = g.allocateTelegraph(TeleSource.Boss, BossKind.Treant);
      this.xSlots[i] = slot;
      const t = g.telegraphs[slot];
      t.tick = 0; t.x = b.x; t.z = b.z; t.angle = facing; t.width = 0;
      if (kind === "charge") {
        const C = BOSS_EXTRA.charge;
        t.shape = TeleShape.Line; t.radius = C.length; t.width = C.width; t.total = C.windup;
        this.xDue[i] = C.windup;
      } else if (kind === "quake") {
        const Q = BOSS_EXTRA.quake;
        t.shape = TeleShape.Circle; t.radius = Q.radius + (b.radius - BOSS.radius) * 0.5; t.total = Q.windup;
        this.xDue[i] = Q.windup;
      } else if (kind === "lance3") {
        // 창날 3줄(C2 경비대장): 좌→중→우 차례로 찌른다. 첫 줄 뒤 빈 줄로 옮겨 서면 된다
        const L3 = BOSS_EXTRA.lance3;
        t.shape = TeleShape.Line; t.radius = L3.length + (b.radius - BOSS.radius); t.width = L3.width;
        t.angle = facing + deg((i - 1) * L3.spreadDeg);
        t.total = L3.windup + i * L3.gap;
        this.xDue[i] = t.total;
      } else if (kind === "execute") {
        // 처형(C2 심연 집행자): 경고가 긴 넓은 정면 한 줄
        const X = BOSS_EXTRA.execute;
        t.shape = TeleShape.Line; t.radius = X.length + (b.radius - BOSS.radius); t.width = X.width; t.total = X.windup;
        this.xDue[i] = X.windup;
      } else {
        const B3 = BOSS_EXTRA.bite3;
        t.shape = TeleShape.Cone; t.radius = B3.range + (b.radius - BOSS.radius); t.width = deg(B3.arcDeg);
        t.angle = facing + deg((i - 1) * B3.spreadDeg);
        t.total = B3.windup + i * B3.gap;
        this.xDue[i] = t.total;
      }
    }
    this.patternEvent(EXTRA_ID[kind]);
    let longest = 0;
    for (let i = 0; i < need; i++) longest = Math.max(longest, this.xDue[i]);
    this.startEcho(EXTRA_ID[kind], longest);
    return true;
  }

  private stepExtra(): void {
    const g = this.g, b = g.boss, h = g.hero, kind = this.xKind!;
    for (let i = 0; i < this.xCount; i++) {
      const slot = this.xSlots[i];
      if (slot < 0) continue;
      const t = g.telegraphs[slot];
      t.tick = Math.min(t.total, b.stateTick + g.worldFrac);
      if (b.stateTick < this.xDue[i]) continue;
      const hit = telegraphHit(t, h.x, h.z);
      g.fireTelegraph(slot); this.xSlots[i] = -1; this.xResolved++;
      const mult = BOSS_EXTRA[kind].dmg;
      // 같은 패턴의 여러 경고는 한 번만 맞힌다(세 갈래 물기도 한 번)
      if (hit && !this.xHit) {
        this.xHit = true;
        if (g.combat.hurtHero(BOSS.damage * mult * this.dmg, b.x, b.z, true, 1)) g.hazards.afflict(g.map.hazardTheme);
        if (!combatScene(g.flow.scene)) return;
      }
      if (kind === "charge") { this.dashAngle = t.angle; this.dashLeft = t.radius - b.radius * 0.5; }
    }
    if (this.xResolved < this.xCount) return;
    const dash = kind === "charge" ? this.dashLeft : 0;
    this.xKind = null;
    this.setState(kind === "quake" || kind === "charge" || kind === "execute" ? BossState.Slam : BossState.Sweep);
    this.dashLeft = dash;
    this.cooldown = this.nextCooldown();
  }

  private cancelEcho(): void {
    for (let i = 0; i < this.echoSlots.length; i++) {
      this.g.releaseTelegraph(this.echoSlots[i]); this.echoSlots[i] = -1;
    }
    if (this.g.map.threatClock) this.g.threats.cancel(ECHO_OWNER);
    this.echoKind = null; this.echoCount = 0; this.echoTick = 0; this.echoDue = 0;
  }

  /**
   * 광폭 두 번째 패턴(메아리) 시작: 첫 패턴(primary = Ev.BossPattern 번호, primaryWindup = 첫 패턴 마지막 판정까지 틱)과 같은 순간에 경고를 깐다.
   * 판정은 max(첫 패턴, 자기 원래 경고) + gap 뒤 — 원래 경고보다 짧지 않다. R2 판정 예약표가 있는 지도는 겹치지 않는 판정 틱을 gap 간격으로 찾고, 못 찾으면 이번엔 건너뛴다.
   * 슬롯이 모자라도 건너뛴다(첫 패턴이 우선). 같은 메아리의 여러 경고(균열 3줄)는 한 번만 맞힌다.
   */
  private startEcho(primary: number, primaryWindup: number): void {
    const g = this.g, b = g.boss;
    if (b.rage !== BossRage.Frenzy || this.echoKind !== null) return;
    const arch = this.def.archetype, kind = echoFor(arch, primary);
    const need = kind === "lines" ? BOSS_EMBER.fissureCount : 1;
    let free = 0;
    for (let i = 0; i < g.telegraphs.length; i++) if (!g.telegraphs[i].active) free++;
    if (free < need) return;
    let total = echoWindup(arch, kind, primaryWindup);
    if (g.map.threatClock) {
      let ok = false;
      for (let k = 0; k < 4 && !ok; k++) {
        ok = g.threats.reserve(ECHO_OWNER, g.threats.now + total);
        if (!ok) total += HAZARD.minResolveGap;
      }
      if (!ok) return;
    }
    this.echoKind = kind; this.echoCount = need; this.echoTick = 0; this.echoDue = total;
    const ember = arch === "ember", grow = Math.max(0.75, Math.min(1.2, b.radius / BOSS.radius));
    for (let i = 0; i < need; i++) {
      const slot = g.allocateTelegraph(TeleSource.Boss, b.kind);
      this.echoSlots[i] = slot;
      const t = g.telegraphs[slot];
      t.tick = 0; t.total = total; t.angle = b.facing; t.width = 0;
      if (kind === "mark") {
        t.shape = TeleShape.Circle; t.x = g.hero.x; t.z = g.hero.z; t.radius = ember ? BOSS_EMBER.meteorRadius : SLAM_RADIUS * grow;
      } else if (kind === "ring") {
        t.shape = TeleShape.Circle; t.x = b.x; t.z = b.z; t.radius = BOSS_EXTRA.quake.radius + (b.radius - BOSS.radius) * 0.5;
      } else {
        // 균열 3줄: 영웅 쪽을 가운데로 부채처럼(시전형 균열과 같은 모양)
        const a = Math.atan2(g.hero.x - b.x, g.hero.z - b.z);
        t.shape = TeleShape.Line; t.x = b.x; t.z = b.z; t.radius = BOSS_EMBER.fissureLength; t.width = BOSS_EMBER.fissureWidth;
        t.angle = a + deg((i - 1) * BOSS_EMBER.fissureSpreadDeg);
      }
    }
    const e = g.emit(Ev.BossPattern);
    e.x = b.x; e.z = b.z; e.a = RAGE_EVENT.echo; e.b = b.kind; e.c = b.variant;
  }

  /** 메아리 진행: 경고를 채우고 판정 틱에 함께 터뜨린다(첫 패턴 상태와 무관, 여러 줄이어도 한 번만 맞힌다) */
  private stepEcho(): void {
    const g = this.g, b = g.boss, h = g.hero, kind = this.echoKind!;
    const done = this.echoTick >= this.echoDue;
    let hit = false;
    for (let i = 0; i < this.echoCount; i++) {
      const slot = this.echoSlots[i];
      if (slot < 0) continue;
      const t = g.telegraphs[slot];
      t.tick = Math.min(t.total, this.echoTick + g.worldFrac);
      if (!done) continue;
      hit = hit || telegraphHit(t, h.x, h.z);
      g.fireTelegraph(slot); this.echoSlots[i] = -1;
    }
    if (!done) return;
    this.echoKind = null; this.echoCount = 0;
    if (hit) {
      const ember = this.def.archetype === "ember";
      const base = kind === "ring" ? BOSS.damage * BOSS_EXTRA.quake.dmg : kind === "lines" ? BOSS_EMBER.fissureDamage : ember ? BOSS_EMBER.meteorDamage : BOSS.damage;
      g.combat.hurtHero(base * this.dmg, b.x, b.z, !ember, ember ? 2 : 1);
    }
  }

  /** 돌진: 경고선을 따라 dashTicks 동안 이동(벽·기둥에서 멈춘다) */
  private stepDash(rate: number): void {
    const g = this.g, b = g.boss;
    const step = Math.min(this.dashLeft, (BOSS_EXTRA.charge.length / BOSS_EXTRA.charge.dashTicks) * rate);
    const sx = Math.sin(this.dashAngle) * step, sz = Math.cos(this.dashAngle) * step;
    let moved = false;
    if (g.walkableBoss(b.x + sx, b.z, b.radius)) { b.x += sx; moved = true; }
    if (g.walkableBoss(b.x, b.z + sz, b.radius)) { b.z += sz; moved = true; }
    this.dashLeft = moved ? this.dashLeft - step : 0;
    if (this.dashLeft <= 1e-3 || b.stateTick >= BOSS_EXTRA.charge.dashTicks + BOSS_TIMING.strike) { this.dashLeft = 0; this.setState(BossState.Chase); }
  }
}
